import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Vault API client (SCRUM-382) — thin fetch wrappers over the SCRUM-373
 * backend. Follows the established conventions (referrals-api.ts): relative
 * /api/* URL constants (tenant from the App ID token claim), res.ok guard, zod-validated
 * responses.
 *
 * Downloads are NOT fetched here: GET /api/vault/{id}/download answers with a
 * 302 to a short-lived presigned COS URL (or the stored source URL for URL
 * references), so the browser navigates to it directly — see
 * `vaultDownloadUrl` and the row-click handler in VaultPage.
 */
const VAULT_URL = '/api/vault';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

// Tenant ID for the Meridian Community Bank tenant (seeded by bankiq-db-seed CE Job).
// SCRUM-643: authenticated /api/* calls derive the tenant from the App ID token
// claim (SCRUM-102); token-less navigations (download) pass the real tenant explicitly.

const vaultDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  sizeBytes: z.number(),
  contentType: z.string(),
  source: z.enum(['file', 'url']),
  sourceUrl: z.string().nullable(),
  uploadedBy: z.string(),
  createdAt: z.string(),
});

export type VaultDocument = z.infer<typeof vaultDocumentSchema>;

/** Error payload shape shared by all vault failure responses (VaultFailure map). */
const vaultErrorSchema = z.object({ error: z.string(), message: z.string() });

async function throwVaultError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const parsed = vaultErrorSchema.safeParse(await res.json());
    if (parsed.success) message = parsed.data.message;
  } catch {
    // Non-JSON body (e.g. a bare 413 from Kestrel) — keep the fallback.
  }
  throw new Error(message);
}

/** GET /api/vault — the tenant's documents, newest first. */
export async function fetchVaultDocuments(): Promise<VaultDocument[]> {
  const res = await apiFetch(VAULT_URL);
  if (!res.ok) {
    throw new Error(`GET ${VAULT_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return vaultDocumentSchema.array().parse(raw);
}

/**
 * POST /api/vault (multipart) — uploads one file. uploadedBy is the demo-era
 * client-supplied identity (same convention as referral actorEmail; replaced
 * by the JWT identity with SCRUM-102/103).
 */
export async function uploadVaultFile(file: File, uploadedBy: string): Promise<VaultDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('uploadedBy', uploadedBy);
  const res = await apiFetch(VAULT_URL, { method: 'POST', body: form });
  if (!res.ok) {
    await throwVaultError(res, `Upload of ${file.name} failed (${String(res.status)}).`);
  }
  const raw: unknown = await res.json();
  return vaultDocumentSchema.parse(raw);
}

/** POST /api/vault/url — records a URL reference (no file stored). */
export async function addVaultUrlReference(
  name: string,
  url: string,
  uploadedBy: string,
): Promise<VaultDocument> {
  const res = await apiFetch(`${VAULT_URL}/url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, url, uploadedBy }),
  });
  if (!res.ok) {
    await throwVaultError(res, `Adding ${name} failed (${String(res.status)}).`);
  }
  const raw: unknown = await res.json();
  return vaultDocumentSchema.parse(raw);
}

/** DELETE /api/vault/{id} — removes the row and (for files) the COS object. */
export async function deleteVaultDocument(id: string): Promise<void> {
  const res = await apiFetch(`${VAULT_URL}/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    await throwVaultError(res, `Delete failed (${String(res.status)}).`);
  }
}

/**
 * The download endpoint 302s to a presigned COS URL / the stored source URL, so
 * downloads open via window.open (top-level navigation — no CORS involved)
 * rather than fetch. A plain navigation cannot carry the App ID Bearer, so the
 * tenant rides the `tenantId` query parameter, which TenantResolutionMiddleware
 * accepts as a navigation fallback for token-less requests.
 *
 * SCRUM-643: the caller passes the AUTHENTICATED tenant (from the auth store's
 * user.tenantId), not a hard-coded demo id. This remains a client-supplied value
 * (the navigation carries no token); hardening the download to a token-bearing
 * request is tracked separately with the SSE-stream auth follow-up.
 */
export function vaultDownloadUrl(id: string, tenantId: string): string {
  return `${VAULT_URL}/${id}/download?tenantId=${encodeURIComponent(tenantId)}`;
}
